import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { PageTopBar } from '~components/page-top-bar';
import { useApplicationSelectionStore, useAuthStore } from '~contexts/mobx';
import { useNavigation } from '~utils/use-navigation';
import { SectionHeader } from '~analytics-components/section-header';
import { KpiLoadProgressBar } from '~analytics-components/kpi-load-progress-bar';
import { KpiCardFromSpec } from '../kpis/kpi-card-from-spec';
import { OverviewToolbar } from '~containers/analytics/overview-page/overview-toolbar';
import { lastSelectableDay } from '~containers/analytics/kpis/periods';
import { usePageFilter } from '~containers/analytics/overview-page/use-page-filter';
import type { PageFilterQuery } from '~containers/analytics/overview-page/use-page-filter';
import { useSearchParams } from '~utils/use-search-params';
import {
    ACQUISITION_CARDS,
    ACTION_CARDS,
    RETENTION_CARDS,
    ACTIVE_USERS_CARDS,
} from './cards';
import { AnalyticsSearchOptions } from '~components/analytics-search-options';
import style from './style.module.scss';

/**
 * Users dashboard. Same filter contract as the Overview and Acquisition pages:
 * one page-level Period + Region, and a compare toggle whose absence means on
 * (only an explicit `?compare=false` turns it off). Every card fetches its own
 * envelope through `KpiCardFromSpec`; there is no page-level fetch any more.
 */
export const UsersAnalyticsPage = observer(() => {
    const authStore = useAuthStore();
    const navigation = useNavigation();

    const applicationSelectionStore = useApplicationSelectionStore();
    const search = useSearchParams<PageFilterQuery>();
    const {
        filter,
        setPeriod,
        setPreset,
        setRegion,
        setCompare,
    } = usePageFilter({
        search,
        region: {
            appTarget: applicationSelectionStore.appTarget,
            defaultProvincesCsv:
                applicationSelectionStore.getProvincesFromSelectedApplication,
        },
    });

    useEffect(() => {
        if (
            !authStore.hasManagementAccess &&
            !authStore.isCustomerServiceAgent
        ) {
            navigation.goToHome();
        }
    }, [
        authStore.hasManagementAccess,
        authStore.isCustomerServiceAgent,
        navigation,
    ]);

    return (
        <>
            <PageTopBar title="Users" />
            <div className={`${style.dashboard} content_with_margins`}>
                <OverviewToolbar
                    filters={
                        <AnalyticsSearchOptions
                            startDate={filter.range.start}
                            endDate={filter.range.end}
                            updateParams={setPeriod}
                            lastSelectableDay={lastSelectableDay()}
                            periodPresets={{
                                active: filter.preset,
                                onSelect: setPreset,
                            }}
                            regionFilter={{
                                value: filter.provinces,
                                disabled: filter.regionLocked,
                                onChange: setRegion,
                            }}
                            compareEnabled={filter.compare}
                            onCompareChange={setCompare}
                        />
                    }
                />

                <KpiLoadProgressBar />

                <section className={style.section}>
                    <SectionHeader title="User acquisition" />
                    <div className={style.dashboard_grid}>
                        {ACQUISITION_CARDS.map((spec, index) => (
                            <KpiCardFromSpec
                                key={`${spec.source}-${spec.form}-${index}`}
                                spec={spec}
                                pageRange={filter.range}
                                pageRegion={filter.provinces}
                                compareEnabled={filter.compare}
                                waveKey={filter.waveKey}
                            />
                        ))}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader title="User actions" />
                    <div className={style.dashboard_grid}>
                        {ACTION_CARDS.map((spec, index) => (
                            <KpiCardFromSpec
                                key={`${spec.source}-${spec.form}-${index}`}
                                spec={spec}
                                pageRange={filter.range}
                                pageRegion={filter.provinces}
                                compareEnabled={filter.compare}
                                waveKey={filter.waveKey}
                            />
                        ))}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader title="Retention pulse" />
                    <div className={style.dashboard_grid}>
                        {RETENTION_CARDS.map((spec, index) => (
                            <KpiCardFromSpec
                                key={`${spec.source}-${spec.form}-${index}`}
                                spec={spec}
                                pageRange={filter.range}
                                pageRegion={filter.provinces}
                                compareEnabled={filter.compare}
                                waveKey={filter.waveKey}
                            />
                        ))}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader title="Active users" />
                    <div className={style.dashboard_grid}>
                        {ACTIVE_USERS_CARDS.map((spec, index) => (
                            <KpiCardFromSpec
                                key={`${spec.source}-${spec.form}-${index}`}
                                spec={spec}
                                pageRange={filter.range}
                                pageRegion={filter.provinces}
                                compareEnabled={filter.compare}
                                waveKey={filter.waveKey}
                            />
                        ))}
                    </div>
                </section>
            </div>
        </>
    );
});
